/**
 * CardStep Component - Simplified Design matching existing TravelSwitch card display
 * Clean, minimalist layout with row-based structure
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BankCard } from '../../types';
import { getEnvironment } from '../../utils/config';
import './CardStep.css';

interface CardStepProps {
  card: BankCard | null;
  loading: boolean;
  error: string | null;
}

const CardStep: React.FC<CardStepProps> = ({ card, loading, error }) => {
  const { t } = useTranslation();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showCvv, setShowCvv] = useState(false);
  const [showCardNumber, setShowCardNumber] = useState(false);

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatCardNumber = (cardNumber: string) => {
    if (!cardNumber) return '';
    // Remove spaces for processing
    const cleanNumber = cardNumber.replace(/\s/g, '');
    // Format with spaces every 4 digits
    return cleanNumber.match(/.{1,4}/g)?.join(' ') || cleanNumber;
  };

  const maskCardNumber = (cardNumber: string) => {
    if (!cardNumber) return '';
    // Remove spaces for processing
    const cleanNumber = cardNumber.replace(/\s/g, '');
    if (cleanNumber.length < 10) return formatCardNumber(cardNumber); // Don't mask if too short

    // Show first 6 and last 4 digits, mask the middle
    const first6 = cleanNumber.substring(0, 6);
    const last4 = cleanNumber.substring(cleanNumber.length - 4);
    const middleLength = cleanNumber.length - 10;
    const masked = first6 + '•'.repeat(middleLength) + last4;

    // Format with spaces every 4 digits
    return masked.match(/.{1,4}/g)?.join(' ') || masked;
  };

  const formatExpiryDate = (month: string, year: string) => {
    if (!month || !year) return '';
    return `${month.padStart(2, '0')}/${year}`;
  };

  const environment = getEnvironment() || 'local';
  const isSandbox = environment !== 'production';

  if (loading) {
    return (
      <div className="card-container">
        <div className="widjet-container">
          <h1>{t('card.title')}</h1>
          <div className="loading-container">
            <div className="spinner"></div>
            <p>{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-container">
        <div className="widjet-container">
          <h1>{t('card.title')}</h1>
          <div className="alert alert-error">
            <strong>{t('common.error')}</strong>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!card) {
    return null;
  }

  const getBrandIconName = (brandValue: string): string => {
    const brand = brandValue.toLowerCase().trim();

    // Exact mappings from CardTypes.cs enum
    const validBrands = [
      'amex',
      'bc',
      'dankort',
      'diners',
      'discover',
      'electron',
      'elo',
      'enroute',
      'hipercard',
      'jcb',
      'laser',
      'maestro',
      'mastercard',
      'solo',
      'switch',
      'troy',
      'uatp',
      'union_pay',
      'visa',
      'visa_electron'
    ];

    return validBrands.includes(brand) ? brand : 'default';
  };

  const getBrandClass = () => {
    const iconName = getBrandIconName(card.brand || card.cardType || '');
    return iconName;
  };

  const getBrandLogo = () => {
    const iconName = getBrandIconName(card.brand || card.cardType || '');
    const iconUrl = `https://static.pcibooking.net/Images/CardTypesIcons/${iconName}.png`;

    return (
      <img
        src={iconUrl}
        alt={card.brand || card.cardType || 'Card'}
        className="brand-icon"
        onError={(e) => {
          // Fallback to default icon if image fails to load
          e.currentTarget.src = 'https://static.pcibooking.net/Images/CardTypesIcons/default.png';
        }}
      />
    );
  };

  return (
    <div className="card-container">
      <div className="widjet-container">
        <h1>{t('card.title')}</h1>

        <div className="card-content">
          {/* Visual Credit Card */}
          <div className="credit-card-visual">
            <div className={`credit-card ${getBrandClass()}`}>
              <div className="card-chip"></div>
              <div className="card-brand-logo">{getBrandLogo()}</div>
              <div className="card-number-display">
                {card.cardNumber ? (showCardNumber ? formatCardNumber(card.cardNumber) : maskCardNumber(card.cardNumber)) : ''}
              </div>
              <div className="card-info">
                <div className="card-holder">
                  <div className="card-label">CARDHOLDER</div>
                  <div className="card-value">{(card.cardholderName || 'CARD HOLDER').toUpperCase()}</div>
                </div>
                <div className="card-expiry">
                  <div className="card-label">EXPIRES</div>
                  <div className="card-value">{formatExpiryDate(card.expiryMonth, card.expiryYear)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Card Details Form */}
          <div className="card-details-form">
            {/* Card Number */}
            <div className="form-field">
              <label className="field-label">{t('card.cardNumber').toUpperCase()}</label>
              <div className="field-input-group">
                <span className="field-value">
                  {card.cardNumber ? (showCardNumber ? formatCardNumber(card.cardNumber) : maskCardNumber(card.cardNumber)) : ''}
                </span>
                <div className="field-actions">
                  {card.cardNumber && (
                    <>
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => setShowCardNumber(!showCardNumber)}
                        title={showCardNumber ? 'Hide card number' : 'Show card number'}
                      >
                        <i className={showCardNumber ? "fa-solid fa-eye-slash" : "fa-solid fa-eye"}></i>
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => copyToClipboard(card.cardNumber?.replace(/\s/g, '') || '', 'cardNumber')}
                        title={copiedField === 'cardNumber' ? String(t('card.copySuccess')) : 'Copy'}
                      >
                        <i className={copiedField === 'cardNumber' ? "fa-solid fa-check" : "fa-solid fa-copy"}></i>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Cardholder Name */}
            <div className="form-field">
              <label className="field-label">{t('card.cardholderName').toUpperCase()}</label>
              <div className="field-input-group">
                <span className="field-value">{card.cardholderName || ''}</span>
                <div className="field-actions">
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => copyToClipboard(card.cardholderName || '', 'cardholderName')}
                    title={copiedField === 'cardholderName' ? String(t('card.copySuccess')) : 'Copy'}
                  >
                    <i className={copiedField === 'cardholderName' ? "fa-solid fa-check" : "fa-solid fa-copy"}></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Expiry and CVV Row */}
            <div className="form-field-row">
              <div className="form-field form-field-half">
                <label className="field-label">{t('card.expiryDate').toUpperCase()}</label>
                <div className="field-input-group">
                  <span className="field-value">{formatExpiryDate(card.expiryMonth, card.expiryYear)}</span>
                  <div className="field-actions">
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => copyToClipboard(formatExpiryDate(card.expiryMonth, card.expiryYear), 'expiry')}
                      title={copiedField === 'expiry' ? String(t('card.copySuccess')) : 'Copy'}
                    >
                      <i className={copiedField === 'expiry' ? "fa-solid fa-check" : "fa-solid fa-copy"}></i>
                    </button>
                  </div>
                </div>
              </div>

              <div className="form-field form-field-half">
                <label className="field-label">
                  {t('card.cvv').toUpperCase()}
                  <i
                    className="fa-solid fa-circle-question tooltip-icon"
                    title="CVV is an anti-fraud measure used by credit card companies worldwide."
                  ></i>
                </label>
                <div className="field-input-group">
                  {(() => {
                    const hasCvv = card.cvv && card.cvv !== '' && card.cvv !== '***';
                    return (
                      <>
                        <span className={`field-value ${!hasCvv ? 'not-available' : ''}`}>
                          {hasCvv ? (showCvv ? card.cvv : '•••') : t('card.notAvailable')}
                        </span>
                        <div className="field-actions">
                          {hasCvv && (
                            <>
                              <button
                                type="button"
                                className="icon-btn"
                                onClick={() => setShowCvv(!showCvv)}
                                title={showCvv ? 'Hide CVV' : 'Show CVV'}
                              >
                                <i className={showCvv ? "fa-solid fa-eye-slash" : "fa-solid fa-eye"}></i>
                              </button>
                              <button
                                type="button"
                                className="icon-btn"
                                onClick={() => copyToClipboard(card.cvv || '', 'cvv')}
                                title={copiedField === 'cvv' ? String(t('card.copySuccess')) : 'Copy'}
                              >
                                <i className={copiedField === 'cvv' ? "fa-solid fa-check" : "fa-solid fa-copy"}></i>
                              </button>
                            </>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Card Type */}
            {(card.cardType || card.brand) && (
              <div className="form-field">
                <label className="field-label">{t('card.cardType').toUpperCase()}</label>
                <div className="field-input-group">
                  <span className="field-value">
                    {(card.brand || card.cardType || 'Unknown').toUpperCase()}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Close Button */}
          <div className="card-footer">
            <button
              type="button"
              className="btn-close"
              onClick={() => window.close()}
            >
              {t('card.close')}
            </button>
          </div>

          {/* PCI Booking Badge */}
          <div className="pci-badge">
            <img
              src="https://static.pcibooking.net/Images/SecuredByPciBooking.png"
              alt="Secured by PCI Booking"
            />
            {isSandbox && (
              <div className="sandbox-label">
                {environment.toUpperCase()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardStep;
